import axios, { AxiosRequestConfig } from 'axios';
import { useEffect, useState } from 'react';

export function useAxios<T = any>(config: AxiosRequestConfig, defaultValue?: T) {
    const [isLoading, setLoading] = useState(false);
    const [source] = useState(axios.CancelToken.source);
    const [data, setData] = useState<T>(defaultValue as T);

    useEffect(() => {
        setLoading(true);
        setData(defaultValue);
        axios({ ...config, cancelToken: source.token })
            .then((res) => res.data)
            .then(setData)
            .finally(() => setLoading(false));

        return () => {
            source.cancel('unmounted');
        };
    }, []);

    return [data, isLoading] as [T, boolean];
}

export function useLazyAxios<T>(config?: AxiosRequestConfig, defaultValue = {}) {
    const [isLoading, setLoading] = useState(false);
    const [source] = useState(axios.CancelToken.source);
    const [data, setData] = useState<T>(defaultValue as T);

    async function request(rewriteConfig?: AxiosRequestConfig) {
        setLoading(true);
        return await axios({ ...config, cancelToken: source.token, ...rewriteConfig })
            .then((res) => res.data)
            .then((data) => {
                setData(data);
                return data;
            })
            .finally(() => setLoading(false));
    }

    return [request, data, isLoading, setData] as [
        (config?: AxiosRequestConfig) => Promise<T>,
        T,
        boolean,
        React.Dispatch<React.SetStateAction<T>>
        ];
}

export function useTest<T>(defaultValue: Partial<T>, mock?: T, timeout?: number) {
    const [isLoading, setLoading] = useState(false);
    const [data, setData] = useState<Partial<T>>(defaultValue as T);

    useEffect(() => {
        setLoading(true);

        const id = setTimeout(() => {
            setData(mock as T);
            setLoading(false);
        }, timeout || 200);

        return () => {
            clearTimeout(id);
        };
    }, []);

    return [data, isLoading] as [T, boolean];
}

export function useLazyTest<T>(defaultValue: Partial<T>, mock?: T, timeout?: number) {
    const [isLoading, setLoading] = useState(false);
    const [timeoutId, setTimeoutId] = useState<any>();
    const [data, setData] = useState<Partial<T>>(defaultValue as T);

    useEffect(() => {
        return () => {
            clearTimeout(timeoutId);
        };
    }, []);

    function request() {
        setLoading(true);

        const timeoutId = setTimeout(() => {
            setData(mock as T);
            setLoading(false);
        }, timeout || 200);

        setTimeoutId(timeoutId);
    }

    return [request, data, isLoading] as [() => void, T, boolean];
}
